/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
  MRT_ToggleFullScreenButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton, MRT_GlobalFilterTextField
} from 'material-react-table';
import styled from 'styled-components';

import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PageviewOutlined as ViewDetailsIcon
} from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import { tableButtonGray } from '../../../../constants/colors';
import { IComponent } from '../../../components/Global/Dialog/GlobalDialog';
import { goToAddStudyDialog, goToDeleteStudyDialog, goToEditStudyDialog } from './Table';
import { EUserPermissionAccess } from "../../../../redux/ducks/AccessControl/types";
import { setGlobalDialogComponent, setIsGlobalDialogOpen } from '../../../../redux/ducks/GlobalDialog/slice';
import { setGlobalRightClickMenuData, setGlobalRightClickMenuPosition } from '../../../../redux/ducks/GlobalRightClickMenu/slice';
import { coverageRequirementsDetails } from "../constants";
import React from "react";

export interface Row {
  name: string
  description: string
  owner: string
  dateCreated: string
  dateModified: string
  status: string
}

// definition details: https://www.material-react-table.com/docs/api/column-options#filterSelectOptions-column-option
interface ICustomFilterSelectOption {
  text: string
  value: string
}

export interface IFilterSelect {
  owner: ICustomFilterSelectOption[] | string[]
  status: string[]
}

export const Container = styled.div({
  display: 'flex',
  flexFlow: 'column',
  overflow: 'auto',
  height: '87vh', // window - header height,
})

// complete list of available filter mode options: https://www.material-react-table.com/docs/guides/column-filtering#pre-built-mrt-filter-functions
export const defaultTextFilterModeOptions = ["contains", "startsWith", "endsWith", "equals", "notEquals"]
export const defaultTextFilterMode = "contains"

export const getSelectionList = (data: any, key: string) => {
  const rowResults: string[] = data.map((elem: any) => elem[key]).filter((elem: any) => {
    return elem !== undefined
  })
  const subRowResults: string[] = (data.map((elem: any) => elem.subRows?.map((elem: any) => elem[key]))).flat(1).filter((elem: any) => {
    return elem !== undefined
  })
  const combinedResults: string[] = rowResults.concat(subRowResults)
  const set = new Set(combinedResults)
  const res = [...set]
  return (
    res
  )
}

export const getCustomSelectionList = (data: any, key: string, userIdNameMap: any) => {
  const values = getSelectionList(data, key)
  const res = values.map((elem: string) => {
    return {
      text: renderNameString(elem, userIdNameMap),
      value: elem
    }
  })
  return (
    res
  )
}

export const renderNameString = (userId: string, userIdNameMap: any) => {
  return (
    userIdNameMap[userId] ?? ""
  )
}

export const tableRowActions = (row: any, userId: any, isAdmin: boolean, userAccessLevel: string, dispatch: any,setOpenDeleteDialog: any, shouldDeleteStudy: any) => {
  const id: string = row.original.id
  const name: string = row.original.name
  const description: string = row.original.description
  const owner: string = row.original.owner
  const isPlanOfRecord: boolean = row.original.isPlanOfRecord
  const isStudyArchive: boolean = row.original.isStudyArchive
  const studyId: string = id
  const hasWriteAccess: boolean = userAccessLevel === EUserPermissionAccess.READ_WRITE
  const isOwner: boolean = userId === row.original.owner
  const url = `${coverageRequirementsDetails}?studyId=${studyId}`

  return (
    <Box
      // Show row actions only on parent rows
      sx={{
        display: row.subRows != undefined ? 'flex' : 'none',
        flexWrap: 'nowrap'
      }}
    >
      <Tooltip title={"View Details"} arrow>
        <a
          onClick={(e) => {
            e.preventDefault()
            const component = {
              'params': { studyId },
              'name': 'CoverageRequirements'
            }
            dispatch(setGlobalDialogComponent(component))
            dispatch(setIsGlobalDialogOpen(true))
          }}
          onContextMenu={(e) => {
            e.preventDefault()
            dispatch(setGlobalRightClickMenuData({ url }))
            dispatch(setGlobalRightClickMenuPosition({ x: e.pageX, y: e.pageY }))
          }}
          href={url}
          target={'_blank'}
        >
          <IconButton size="large">
            <ViewDetailsIcon fontSize="medium" style={{ color: tableButtonGray }} />
          </IconButton>
        </a>
      </Tooltip>

      {(isAdmin || (hasWriteAccess && isOwner)) && <Tooltip title={"Edit"} arrow>
        <a
          onClick={(e) => {
            e.preventDefault()
            const component: IComponent = {
              'params': { id, name, description, owner, isPlanOfRecord, isStudyArchive  },
              'name': 'CoverageRequirementsEditStudyDetails',
            }
            goToEditStudyDialog(dispatch, component)
          }}
        >
          <IconButton size="large">
            <EditIcon fontSize="medium" style={{ color: tableButtonGray }} />
          </IconButton>
        </a>
      </Tooltip>}

      {(isAdmin || (hasWriteAccess && isOwner)) && !isStudyArchive && <Tooltip title={"Delete"} arrow>
        <a
          onClick={(e) => {
            setOpenDeleteDialog({initialLoad: true})
            shouldDeleteStudy(row?.original)
            // goToDeleteStudyDialog(dispatch, component)}
          }}
        >
          <IconButton size="large">
            <DeleteIcon fontSize="medium" style={{ color: tableButtonGray }} />
          </IconButton>
        </a>
      </Tooltip>}

      {/*{(isAdmin || hasWriteAccess) && <Tooltip title={"Copy"} arrow>*/}
      {/*  <a*/}
      {/*    onClick={(e) => {*/}

      {/*    }}*/}
      {/*  >*/}
      {/*    <IconButton size="large">*/}
      {/*      <ContentCopyIcon fontSize="medium" style={{ color: tableButtonGray }} />*/}
      {/*    </IconButton>*/}
      {/*  </a>*/}
      {/*</Tooltip>}*/}
    </Box>
  )
}

export const tableToolbarActions = (table: any, isAdmin: boolean, userAccessLevel: string, dispatch: any) => {
  const hasWriteAccess: boolean = userAccessLevel === EUserPermissionAccess.READ_WRITE

  return (
    <Box>
      {(isAdmin || hasWriteAccess) && <Tooltip title="Add Study" arrow placement="bottom">
        <IconButton onClick={() => goToAddStudyDialog(dispatch)} size="medium">
          <AddIcon style={{ color: tableButtonGray }} />
        </IconButton>
      </Tooltip>}
      <MRT_ToggleGlobalFilterButton table={table} style={{ color: tableButtonGray }} />
      <MRT_ToggleFiltersButton table={table} style={{ color: tableButtonGray }} />
      <MRT_ToggleFullScreenButton table={table} style={{ color: tableButtonGray }} />
      <MRT_ShowHideColumnsButton table={table} style={{color: tableButtonGray}} />
    </Box>
  )
}